'use client';

import {Button} from '@udemy-v2/react-core-components';
import {Icon} from '@udemy-v2/icons';
import {useI18n} from '@udemy-v2/i18n';
import {publishEvent} from '@udemy-v2/event-tracking';

import type {CommunityRenderedPrice} from '@/features/instructor-profile/lib/community-api';
import {
    COMMUNITY_UI_REGION,
    buildJoinSelectedEvent,
} from '@/components/tracking/instructor-community-events';

import styles from './community-benefits.module.scss';

// Public-folder assets are not rewritten by Next.js `assetPrefix`, so we prepend
// it manually. This resolves to the CDN (frontends[-dev].udemycdn.com/<app>) in
// dev/prod and to the app-name path locally, where a rewrite maps it back to
// /public. Trailing slash is stripped so the join never produces `//`.
const ASSET_BASE = `${(process.env.ASSET_PREFIX ?? '').replace(/\/$/, '')}/community-benefits/v1`;

interface CommunityBenefitsProps {
    /**
     * Monthly membership price; rendered in the "For {price} per month" line.
     * Omitted when absent (no published price).
     */
    price?: CommunityRenderedPrice | null;
    /** Membership benefits, one row each with a green check. */
    benefits?: string[];
    /** Community URL for the "Join my community" CTA. */
    communityUrl?: string | null;
    /**
     * Click handler for the "Join my community" CTA. Optional; defaults to a
     * no-op until the join flow is wired up (mirrors {@link CommunityHeader}).
     */
    onJoinClick?: () => void;
    /**
     * Profile owner's user id, for the `InstructorCommunityJoinSelected` event.
     * Supplied on the public profile path only.
     */
    instructorId?: string | null;
    /**
     * Community id, for the same event. Both ids must be present for it to
     * fire, so the instructor DRAFT preview publishes nothing.
     */
    communityId?: string | null;
}

function buildJoinUrl(communityUrl: string): string {
    const url = new URL(communityUrl);
    url.searchParams.set('automatic_login', 'true');
    return url.toString();
}

const noop = () => {};

/** Resolved-locale BCP-47 tag (`en_US` → `en-US`) for the `Intl` APIs. */
function toBcp47(locale?: string): string | undefined {
    return locale ? locale.replace(/_/g, '-') : undefined;
}

/**
 * Benefits / CTA division of the community block — Figma node 4313:74538
 * (desktop) and 2944:42768 (mobile), file `mitwWlKOuf1pyo4gUCLEBY`.
 *
 * A centred card (lined up with the adjacent community banners) with a gradient
 * background: highlighted heading, a monthly price line, a primary "Join my
 * community" CTA, a two-column benefits checklist, and a transparent decorative
 * collage that sits over the gradient — right-aligned on desktop, at the foot on
 * mobile. It renders right after the intro, ahead of the feature banners, on
 * all viewports.
 * Presentational only — the public profile page (PUBLISHED) and the Phase-2
 * preview tab (DRAFT) both feed it already-resolved data. When communityUrl is
 * provided, clicking the button navigates to the community with automatic_login=true search param appended.
 *
 * Selecting the CTA publishes `InstructorCommunityJoinSelected` with
 * `uiRegion: instructor_profile.community_section.benefits`.
 */
export function CommunityBenefits({
    price,
    benefits = [],
    communityUrl,
    onJoinClick,
    instructorId,
    communityId,
}: CommunityBenefitsProps) {
    const {gettext, locale} = useI18n();

    const navigate = onJoinClick ?? (communityUrl ? () => {
        const joinUrl = buildJoinUrl(communityUrl);
        window.location.href = joinUrl;
    } : noop);

    // This block only renders for non-subscribed viewers (see `CommunityBlock`),
    // so every click here is a join.
    const handleJoinClick = () => {
        const event = buildJoinSelectedEvent({
            instructorId,
            communityId,
            uiRegion: COMMUNITY_UI_REGION.sectionBenefits,
        });
        if (event) {
            publishEvent(event);
        }
        navigate();
    };

    // Nothing to show without benefits or a price — keep the section out of the
    // DOM (and off the mobile reorder) entirely.
    if (benefits.length === 0 && !price) {
        return null;
    }

    const formattedPrice = price
        ? new Intl.NumberFormat(toBcp47(locale), {
              style: 'currency',
              currency: price.currency,
          }).format(price.amount)
        : null;

    // Split the price sentence around the placeholder so the amount can be
    // emphasised (medium weight, default colour) while the rest stays light /
    // subdued — keeps the whole sentence as one translatable string.
    const priceParts = formattedPrice
        ? gettext("For %(price)s per month, you'll get:").split('%(price)s')
        : null;

    // A short list reads as one left-aligned column; longer lists use two.
    const singleColumn = benefits.length <= 5;

    return (
        <div className={styles['benefits']} data-purpose="community-benefits">
            <div className={styles['content']}>
                <div className={styles['copy']}>
                    <div className={styles['text-block']}>
                        <h2 className={styles['heading']}>
                            {gettext("See what's included in my ")}
                            <span className={styles['heading-highlight']}>
                                {gettext('community')}
                            </span>
                        </h2>

                        {priceParts ? (
                            <p className={styles['price']}>
                                {priceParts[0]}
                                <span className={styles['price-amount']}>
                                    {formattedPrice}
                                </span>
                                {priceParts[1]}
                            </p>
                        ) : null}
                    </div>

                    <Button
                        udStyle="primary"
                        size="medium"
                        typography="ud-btn-text-md"
                        onClick={handleJoinClick}
                        data-purpose="join-community-benefits"
                        className={styles['cta']}
                        style={{padding: '11.5px 12px'}}
                    >
                        {gettext('Join my community')}
                    </Button>

                    {benefits.length > 0 ? (
                        <ul
                            className={
                                singleColumn
                                    ? `${styles['benefit-list']} ${styles['single-column']}`
                                    : styles['benefit-list']
                            }
                        >
                            {benefits.map((benefit, index) => (
                                // Benefits have no stable id; order is stable within
                                // a rendered view, so index is an acceptable key here.
                                <li key={index} className={styles['benefit']}>
                                    <span className={styles['benefit-icon']}>
                                        <Icon
                                            glyph="tick"
                                            label={false}
                                            color="knockout"
                                            size="xsmall"
                                        />
                                    </span>
                                    <span>{benefit}</span>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </div>
            </div>

            {/* Transparent collage (static asset), right-aligned on desktop /
                bottom on mobile over the section's gradient. The heading and
                benefits convey the meaning, so the image is presentational. */}
            <picture className={styles['art']}>
                <source
                    media="(max-width: 900px)"
                    srcSet={`${ASSET_BASE}/benefits_bg_mobile.png`}
                />
                <img src={`${ASSET_BASE}/benefits_bg_desktop.png`} alt="" />
            </picture>
        </div>
    );
}
